import { useCallback, useEffect, useMemo, useRef } from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import debounce from 'lodash/debounce'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import {
  clearPendingPostComments,
  registerPendingPostComment,
} from '~publish/pages/Community/hooks/comments/pendingPostCommentsSignal'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { POSTS_PER_PAGE } from './usePostsListQuery'
import type { UsePostsListQueryResult } from './usePostsListQuery'

/**
 * Trailing-debounce window applied to Pusher-triggered refetches. Collapses a
 * burst of backfill events into a single refetch.
 */
const PUSHER_REFETCH_DEBOUNCE_MS = 500

/**
 * The producer puts the raw comment entity on the wire, so the post it belongs
 * to arrives nested under `comment`.
 */
/**
 * The producer puts the raw comment entity on the wire, so the id arrives as
 * Mongo's `_id`, with no `id` field to read. The value matches the `id` the
 * GraphQL layer returns for the same comment.
 */
type CommentIngestedEventData = {
  channelId?: string
  comment?: { _id?: string; postId?: string }
}

const GetPostsWithCommentsList = graphql(
  `
    query GetPostsWithCommentsList(
      $first: Int!
      $after: String
      $input: PostsWithCommentInput!
    ) {
      postsWithComments(input: $input, first: $first, after: $after) {
        edges {
          node {
            ...CommentsPostCard_Post
            createdAt
            sentAt
          }
          cursor
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
    }
  `,
  [CommentsPostCard_Post],
)

type UsePostsWithCommentsQueryOptions = {
  channelIds: string[]
  sortDirection?: 'asc' | 'desc'
  perPage?: number
  skip?: boolean
}

export const usePostsWithCommentsQuery = ({
  channelIds,
  sortDirection = 'desc',
  perPage = POSTS_PER_PAGE,
  skip: skipQuery,
}: UsePostsWithCommentsQueryOptions): UsePostsListQueryResult => {
  const organizationId = useOrganizationId()

  const afterCursor: string | null = null

  const { data, dataState, refetch, fetchMore, error, networkStatus } =
    useQuery(GetPostsWithCommentsList, {
      variables: {
        first: perPage,
        after: afterCursor,
        input: {
          organizationId: organizationId || '',
          filters: {
            channelIds,
            status: 'all' as const,
          },
          sort: {
            field: 'publishedAt' as const,
            direction: sortDirection,
          },
        },
      },
      fetchPolicy: 'cache-and-network',
      // cache-first (not cache-only) so `fetchMore` pagination can reach the
      // network; cache-only still serves cached pages without re-fetching.
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: !organizationId || skipQuery,
    })

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables
  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  // ── Real-time updates via Pusher ──────────────────────────────────
  //
  // A finished channel backfill can add posts to this list, which only holds
  // posts that have comments, so the list is reloaded once the backfill
  // reports comments. Debounced because several channels can finish together.
  //
  // An ingested comment does not reload the list. The server returns the list
  // freshly sorted, so reloading on every comment reorders the panel under the
  // user, and the accumulated pages are offset-based, so the same reorder makes
  // the re-paged offsets skip posts. Ingested comments are tallied for the
  // sidepanel pill instead, and the user reloads the list when they choose to.
  //
  // `refetch` and the skip condition are read through refs so the debounced
  // function can be created once (stable reference) while still using the
  // latest values when it fires. The skip condition must mirror `useQuery`'s
  // `skip` (organizationId missing OR caller-requested skip), otherwise a
  // Pusher-triggered refetch could fire a request with `organizationId: ''`.
  const refetchRef = useRef(refetch)
  const skipRef = useRef(!organizationId || Boolean(skipQuery))
  refetchRef.current = refetch
  skipRef.current = !organizationId || Boolean(skipQuery)

  const debouncedRefetch = useMemo(
    () =>
      debounce(
        () => {
          if (skipRef.current) return
          void refetchRef.current()
        },
        PUSHER_REFETCH_DEBOUNCE_MS,
        { leading: false, trailing: true },
      ),
    [],
  )

  useEffect(() => {
    return (): void => {
      debouncedRefetch.cancel()
    }
  }, [debouncedRefetch])

  const isTrackedChannel = useCallback(
    (eventChannelId: string | undefined): boolean => {
      if (!eventChannelId) return true
      return channelIds.includes(eventChannelId)
    },
    [channelIds],
  )

  usePusherEvent(
    PusherEvent.COMMENT_INGESTED,
    (eventData: CommentIngestedEventData) => {
      const postId = eventData?.comment?.postId
      if (!postId || skipRef.current) return
      if (!isTrackedChannel(eventData?.channelId)) return
      registerPendingPostComment(postId, eventData?.comment?._id)
    },
  )

  // The tally describes the list this hook currently holds. A different channel
  // selection, the switch to the unfiltered list, and leaving the by-post view
  // all replace that list, so the count must not follow the user into it.
  const channelScopeKey = channelIds.join(',')
  useEffect(() => {
    clearPendingPostComments()
    return (): void => clearPendingPostComments()
  }, [channelScopeKey, skipQuery])

  usePusherEvent(
    PusherEvent.CHANNEL_BACKFILL_DONE,
    (eventData: { channelId?: string; newCommentsCount?: number }) => {
      if (!eventData?.newCommentsCount || eventData.newCommentsCount <= 0) {
        return
      }
      if (isTrackedChannel(eventData?.channelId)) {
        debouncedRefetch()
      }
    },
  )

  const loadMorePosts = useCallback(async () => {
    if (!data?.postsWithComments?.pageInfo?.hasNextPage) return
    try {
      await fetchMore({
        variables: {
          after: data.postsWithComments.pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.postsWithComments?.edges) return prev

          const newEdges = fetchMoreResult.postsWithComments.edges
          const newPostIds = new Set(
            newEdges.map((edge) => {
              const post = readFragment(CommentsPostCard_Post, edge.node)
              return post.id
            }),
          )
          const existingEdges = (prev.postsWithComments?.edges || []).filter(
            (edge) => {
              const post = readFragment(CommentsPostCard_Post, edge.node)
              return !newPostIds.has(post.id)
            },
          )

          return {
            ...prev,
            postsWithComments: {
              ...prev.postsWithComments,
              edges: [...existingEdges, ...newEdges],
              pageInfo: fetchMoreResult.postsWithComments.pageInfo,
            },
          }
        },
      })
    } catch (err) {
      toast.error('Error fetching more posts')
      debugLog('❌ Failed to fetch more posts:', err)
    }
  }, [
    data?.postsWithComments?.pageInfo?.hasNextPage,
    data?.postsWithComments?.pageInfo?.endCursor,
    fetchMore,
  ])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.postsWithComments?.pageInfo?.hasNextPage,
    fetchMore: loadMorePosts,
  })

  const completeData = dataState === 'complete' ? data : undefined

  const posts = useMemo(() => {
    const edges = completeData?.postsWithComments?.edges || []
    return edges
      .filter((edge) => {
        const post = readFragment(CommentsPostCard_Post, edge.node)
        return post.notificationStatus !== 'notified'
      })
      .map((edge) => edge.node)
  }, [completeData?.postsWithComments?.edges])

  return useMemo(
    () => ({
      posts,
      loading,
      fetchingMore,
      error,
      lastElementRef,
      refetch,
      hasNextPage: data?.postsWithComments?.pageInfo?.hasNextPage ?? false,
      fetchMore: loadMorePosts,
    }),
    [
      posts,
      loading,
      fetchingMore,
      error,
      lastElementRef,
      refetch,
      data?.postsWithComments?.pageInfo?.hasNextPage,
      loadMorePosts,
    ],
  )
}
